import React, { FC, ReactNode, useCallback } from 'react';
import { IconCalendar, IconClock, IconMapPin } from '../icons';
import { useQueryClient } from '@tanstack/react-query';
import { useBrand } from '../../hooks/useBrand';
import { useLocationInternal } from '../../hooks/useLocation';
import { useCurrentWeather } from '../../hooks/weather/useWeather';
import Time from '../NavPanel/Time';
import { WeatherIcon } from '../../hooks/weather/weather';
import { NewsFeed } from './NewsFeed';
import { useNavigateToDefaultView } from '../../hooks/useNavigateToDefaultView';
import { useConfig } from '../../providers/ConfigProvider';
import { cn } from '../../utils/classNames';

const VerticalTopSection: FC = () => {
  const queryClient = useQueryClient();
  const { logo } = useBrand();
  const { organization_id } = useConfig();
  const navigateToDefaultView = useNavigateToDefaultView();

  const handleRefresh = useCallback(() => {
    void queryClient.invalidateQueries();
    navigateToDefaultView();
  }, [navigateToDefaultView, queryClient]);

  const showNewsFeed = organization_id === 'kruthusen';

  if (showNewsFeed) {
    return (
      <div className="flex h-[250px] min-h-[250px] w-full overflow-hidden shadow-md">
        <div className="flex h-full w-[400px] min-w-[400px] flex-col justify-center gap-5 bg-white pb-1">
          <Logo logo={logo} handleRefresh={handleRefresh} />
          <SmallInfoItems />
        </div>

        <NewsFeed />
      </div>
    );
  }

  return (
    <div className="flex h-[250px] min-h-[250px] w-full items-center justify-around overflow-hidden shadow-md">
      <Logo logo={logo} handleRefresh={handleRefresh} />

      <div className="flex h-full items-center justify-center">
        <SmallInfoItems big />
      </div>
    </div>
  );
};

interface LogoProps {
  logo: ReactNode;
  handleRefresh: () => void;
}
const Logo: FC<LogoProps> = ({ logo, handleRefresh }) => {
  return (
    <div
      className="flex h-[150px] min-h-[150px] items-center justify-center overflow-hidden"
      onClick={handleRefresh}
    >
      {logo}
    </div>
  );
};

interface SmallInfoItemsProps {
  big?: boolean;
}
const SmallInfoItems: FC<SmallInfoItemsProps> = ({ big }) => {
  const { data: location, isFetched } = useLocationInternal();
  const currentWeather = useCurrentWeather();

  if (!isFetched || !location) {
    return null;
  }

  return (
    <div className={cn('flex flex-col items-center gap-1', big && 'gap-4')}>
      <div className={cn('flex gap-4 px-2', big && 'gap-8')}>
        <div className="flex max-w-full items-center gap-1">
          <IconMapPin className="h-6 w-6" />
          <span className="line-clamp-1 break-all">
            {location.street || '-'}
          </span>
        </div>
        {currentWeather && (
          <div className="flex items-center gap-1">
            <WeatherIcon
              symbol={currentWeather.symbol}
              className="h-6 w-6 text-white"
            />
            <span>{currentWeather.temperature}&deg;</span>
          </div>
        )}
      </div>
      <div className={cn('flex gap-4', big && 'gap-8')}>
        <div className="flex items-center gap-1">
          <IconCalendar className="h-6 w-6" />
          <span className="capitalize">
            <Time date />
          </span>
        </div>
        <div className="flex items-center gap-1">
          <IconClock className="h-6 w-6" />
          <Time />
        </div>
      </div>
    </div>
  );
};

export default VerticalTopSection;
